/**
 * How the change went — Step 198.
 *
 * Lives in the PROPERTIES RAIL, not the Plan tab. Kai: *"The outcome box also
 * I think should be in the properties bar, not in plan."* He is right, and the
 * reason is that the Plan tab answers *what we intend to do* — written once,
 * before anything happens, and read by approvers. The outcome answers *what
 * actually happened*, recorded after the window, by whoever did the work. They
 * are different questions asked at different times of different people, and
 * burying the second at the bottom of the first is how it gets skipped.
 *
 * ── Why this is one atomic save, not two auto-commits ─────────────────
 *
 * The rest of the rail auto-commits, which is the approved deviation for
 * enumerated single-selects. This card does not, because the note is
 * free-form and — for two of the three outcomes — REQUIRED. Committing the
 * outcome the instant it is picked would create exactly the state the gate
 * then refuses: "completed with issues" on record with no issues written
 * down. So the pair saves together, and draft-first still holds for the text.
 */
import { useEffect, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { ClipboardCheck } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Select } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { apiSend } from '../../lib/api';
import { qk } from '../../lib/queryClient';
import { useDirty } from '../../lib/dirty';
import {
  CHANGE_OUTCOMES,
  CHANGE_OUTCOME_HELP,
  CHANGE_OUTCOME_LABEL,
  CHANGE_OUTCOME_NOTE_LABEL,
  CHANGE_OUTCOME_NOTE_PROMPT,
  outcomeNeedsNote,
  type ChangeOutcome,
} from '@itsm/shared';

export function ChangeOutcomeCard({
  id,
  outcome,
  note,
  canEdit,
}: {
  id: string;
  outcome: string | null;
  note: string | null;
  canEdit: boolean;
}) {
  const qc = useQueryClient();
  const [draftOutcome, setDraftOutcome] = useState(outcome ?? '');
  const [draftNote, setDraftNote] = useState(note ?? '');

  /**
   * Re-seed when the SERVER's value changes, not on a timer and not after our
   * own save. Keyed on the incoming props rather than a latch re-armed in
   * `onSuccess` — that is the Step 192 bug, and it blanked three whole tabs.
   */
  useEffect(() => {
    setDraftOutcome(outcome ?? '');
    setDraftNote(note ?? '');
  }, [outcome, note]);

  const dirty = draftOutcome !== (outcome ?? '') || draftNote !== (note ?? '');
  const clearDirty = useDirty(dirty);

  const needsNote = outcomeNeedsNote(draftOutcome);
  const missing = needsNote && !draftNote.trim();

  const save = useMutation({
    mutationFn: () =>
      apiSend(`/api/tickets/${id}/change`, 'PUT', {
        reviewOutcome: draftOutcome || null,
        // Cleared with the outcome: a note explaining issues that are no
        // longer recorded is worse than no note.
        reviewNote: draftOutcome ? draftNote.trim() || null : null,
      }),
    onSuccess: (r) => {
      if (!r.ok) return toast.error(r.error ?? 'Could not save the outcome.');
      clearDirty();
      toast.success('Outcome saved.');
      void qc.invalidateQueries({ queryKey: qk.tickets.change(id) });
      void qc.invalidateQueries({ queryKey: qk.tickets.detail(id) });
      void qc.invalidateQueries({ queryKey: qk.tickets.activity(id) });
    },
  });

  return (
    <section className="space-y-2 rounded-lg border bg-card p-3">
      <p className="flex items-center gap-1.5 text-xs font-medium uppercase tracking-wider text-muted-foreground">
        <ClipboardCheck className="h-3.5 w-3.5" /> Outcome
      </p>
      <Select
        value={draftOutcome}
        disabled={!canEdit}
        onChange={(e) => setDraftOutcome(e.target.value)}
        aria-label="Change outcome"
      >
        <option value="">Not reviewed yet</option>
        {CHANGE_OUTCOMES.map((o) => (
          <option key={o} value={o}>
            {CHANGE_OUTCOME_LABEL[o]}
          </option>
        ))}
      </Select>
      <p className="text-xs text-muted-foreground">
        {draftOutcome ? CHANGE_OUTCOME_HELP[draftOutcome as ChangeOutcome] : 'Needed before it can close.'}
      </p>

      {/* Only the two outcomes that imply there is something to explain. A
          successful change has nothing owed, and asking anyway trains people
          to type "n/a". */}
      {needsNote && (
        <div>
          <label className="mb-1 block text-xs font-medium">
            {CHANGE_OUTCOME_NOTE_LABEL[draftOutcome as ChangeOutcome]}
            <span className="ml-1 rounded bg-muted px-1.5 text-[11px] font-normal text-muted-foreground">
              required
            </span>
          </label>
          <Textarea
            value={draftNote}
            disabled={!canEdit}
            onChange={(e) => setDraftNote(e.target.value)}
            placeholder={CHANGE_OUTCOME_NOTE_PROMPT[draftOutcome as ChangeOutcome]}
            className="min-h-[72px] text-xs"
          />
        </div>
      )}
      {/* Plain text, not rich text: this is read in a report and in the review,
          and a paragraph is all anybody needs. */}
      {!needsNote && draftNote && (
        <p className="whitespace-pre-wrap text-xs text-muted-foreground">{draftNote}</p>
      )}

      {canEdit && (
        <div className="flex items-center gap-2">
          <Button size="sm" disabled={!dirty || missing || save.isPending} onClick={() => save.mutate()}>
            {save.isPending ? 'Saving…' : 'Save outcome'}
          </Button>
          {missing && (
            <span className="text-xs text-muted-foreground">
              {draftOutcome === 'unsuccessful' ? 'Say why' : 'Say what went wrong'}
            </span>
          )}
        </div>
      )}
    </section>
  );
}
